import Client from './Client';
import type DataStore from './DataStore';
import { observable, randomId } from '@aha-app/mvc';
import { type Constructor, type RecordId } from '../types';
import type WhiteboardController from '../controllers/WhiteboardController';

export type RecordValue = unknown;
export type KeyPath = string | string[];

export type SerializeKey = string | RegExp;

// this interface omits set, as including it causes a mismatch when extending this interface and
// fabric.Object. That doesn't matter when the instance is a fabric.Object, as that includes set anyway
interface DataStoreRecordBehaviors {
  controller: WhiteboardController;
  id: RecordId;
  serializedKeys: SerializeKey[];
  observableKeys: SerializeKey[];
  withoutCollaboration(func: () => void): void;
  collaborate: boolean;

  setFromRemote(key: KeyPath, value: any, options?: Record<string, any>): void;
  uniqueId: () => string;
  valueAtPath: (keyOrPath: KeyPath) => RecordValue;
  _dataStore?: DataStore;

  // needs to match the fabric.Object type for this
  toJSON(propertiesToInclude?: string[] | undefined): any;
}

// this interface should be used in places where we need to know about the set method that
// DataStoreRecordBehaviors adds - i.e. when using the decorator on something other than fabric.Object
export interface IDataStoreRecordBehaviors extends DataStoreRecordBehaviors {
  set(keyOrPath: KeyPath, value?: RecordValue): this;
}

export const isSerializedKey = (
  obj: DataStoreRecordBehaviors,
  key: string
): boolean =>
  obj.serializedKeys.some(
    val =>
      (typeof val === 'string' && val === key) ||
      (val instanceof RegExp && val.test(key))
  );

// Behaviours added into records in a DataStore.
// eslint-disable-next-line @typescript-eslint/no-redeclare
function DataStoreRecordBehaviors(Base: Constructor<any>) {
  return class DataStoreRecordBehaviors
    extends Base
    implements IDataStoreRecordBehaviors
  {
    __hasDataStoreRecordBehaviors = true;
    controller: WhiteboardController;
    id: RecordId;
    serializedKeys: SerializeKey[];
    observableKeys: SerializeKey[];
    collaborate = true;
    _dataStore?: DataStore;

    // Disable collaborative updates for the duration of `func`.
    withoutCollaboration(func: () => void): void {
      try {
        this.collaborate = false;
        func();
      } finally {
        this.collaborate = true;
      }
    }

    // Optimized version of `set` that only sets properties which are changing.
    // key can be an object to set multiple properties at once.
    // key can be an array to set sub-properties.
    set(keyOrPath: KeyPath | Partial<this>, value?: RecordValue): this {
      if (Array.isArray(keyOrPath)) {
        if (this.valueAtPath(keyOrPath) === value) return this;
        this._set(keyOrPath, value);
      } else if (typeof keyOrPath === 'object') {
        for (const prop in keyOrPath) {
          this.set(prop, keyOrPath[prop]);
        }
      } else {
        // For scalar values, only call set if the value is changing.
        if (
          !['number', 'boolean', 'string'].includes(typeof value) ||
          this[keyOrPath] !== value
        ) {
          this._set(keyOrPath, value);
        }
      }

      return this;
    }

    // Hook set operations and send to remote clients too.
    _set(keyOrPath: KeyPath, value: any) {
      const oldValue = this.propertyToJSON(keyOrPath);
      let key: string;

      if (Array.isArray(keyOrPath)) {
        key = keyOrPath[0];
      } else {
        key = keyOrPath;
      }

      if (!super._set) {
        if (Array.isArray(keyOrPath) && keyOrPath.length > 1) {
          let obj = this;
          for (let i = 0; i < keyOrPath.length - 1; i++) {
            if (!(keyOrPath[i] in obj)) {
              obj[keyOrPath[i]] = {};
            } else if (typeof obj[keyOrPath[i]] !== 'object') {
              throw new Error(
                `Attempt to apply sub-property to non-object ${JSON.stringify(
                  obj[keyOrPath[i]]
                )}`
              );
            }
            obj = obj[keyOrPath[i]];
          }

          obj[keyOrPath[keyOrPath.length - 1]] = value;
        } else {
          this[key] = value;
        }
      } else {
        super._set(keyOrPath, value);
      }

      // Wait until canvas is set before sending properties to remote.
      // Otherwise we start sending data during the constructor.
      if (this._dataStore) {
        // Don't send ignored objects.
        if (Client.ignoredObjectTypes.includes(this.type)) return;

        // Only send serialized properties.
        if (!isSerializedKey(this, key)) {
          return;
        }

        const newValue = this.propertyToJSON(keyOrPath);

        // Don't send values that didn't change.
        if (newValue === oldValue) return;

        // Special case for setting 'z' when loading a legacy document with no
        // z set or rebalancing during load.
        const force = key === 'z' && oldValue == null;

        if (this.collaborate) {
          let fullPath = [this._dataStore._tableKey, this.uniqueId()];
          if (Array.isArray(keyOrPath)) {
            fullPath = fullPath.concat(keyOrPath);
          } else {
            fullPath.push(keyOrPath);
          }

          this._dataStore._client?.setObjectProperty(
            fullPath,
            newValue,
            oldValue,
            force
          );
        }

        this._dataStore.recordDidUpdate(this);
      }
    }

    valueAtPath(path: string[]) {
      let value = this;
      for (const key of path) {
        value = value[key];
        if (!value) break;
      }
      return value;
    }

    setFromRemote(
      key: KeyPath,
      value: any,
      options?: Record<string, any>
    ): void {
      if (this.isObservedKeyPath(key)) {
        observable(this).set(key, value);
      } else {
        this.set(key, value);
      }

      if (super.setFromRemote) {
        super.setFromRemote(key, value, options);
      }
    }

    // Allocate a unique ID for this object.
    uniqueId(newId = false) {
      if (newId) {
        this.id = randomId() as RecordId;
      } else {
        this.id ||= randomId() as RecordId;
      }
      return this.id;
    }

    isObservedKeyPath(keyOrPath: KeyPath) {
      if (!this.observableKeys) return false;

      let key: string;
      if (Array.isArray(keyOrPath)) {
        key = keyOrPath[0];
      } else {
        key = keyOrPath;
      }

      if (!key) return false;

      if (this.observableKeys.includes(key)) {
        return true;
      }

      const regexps = this.observableKeys.filter(key => key instanceof RegExp);
      return regexps.some(regexp => regexp.test(key));
    }

    // Serialize this object to JSON.
    toJSON() {
      const json = {
        type: this.type,
        id: this.uniqueId(),
      };

      this.serializedKeys.forEach(key => {
        if (key instanceof RegExp) {
          for (const [propKey] of Object.entries(this)) {
            if (key.test(propKey)) {
              const value = this.propertyToJSON(propKey);
              if (value !== undefined) {
                json[propKey] = value;
              }
            }
          }
        } else {
          const value = this.propertyToJSON(key);
          if (value !== undefined) {
            json[key] = value;
          }
        }
      });

      return json;
    }

    propertyToJSON(keyOrPath: KeyPath) {
      if (Array.isArray(keyOrPath)) return this.valueAtPath(keyOrPath);

      const serializeFunc = this[`${keyOrPath}ToJSON`];
      if (serializeFunc) {
        return serializeFunc.call(this);
      } else {
        return this[keyOrPath];
      }
    }
  };
}

export default DataStoreRecordBehaviors;
